import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { useLocation } from '@reach/router';
import FacebookIcon from '@src/assets/icons/ic_facebook.svg';
import FileTypeDefaultIcon from '@src/assets/icons/ic_file-type_default.svg';
import FileTypePdfIcon from '@src/assets/icons/ic_file-type_pdf.svg';
import FileTypeVideoIcon from '@src/assets/icons/ic_file-type_video.svg';
import TwitterIcon from '@src/assets/icons/ic_twitter.svg';
import CheckoutModalPresenter from '@src/checkout/checkoutModalPresenter';
import DefaultProfile from '@src/components/common/defaultProfile';
import Layout from '@src/components/layout/layout';
import pageSlugs from '@src/components/layout/pageSlugs';
import { localeListInverted } from '@src/i18n/localeList';
import { convertBase64ToString } from '@src/utils/base64';
import { Themed } from '@theme-ui/mdx';
import { Link, graphql, navigate } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Flex,
  Grid,
  Label,
} from 'theme-ui';
import CheckoutMaterialPresenter from '../checkout/material/checkoutMaterialPresenter';
import api from '../utils/api';

export const query = graphql`
  query MaterialBySlug($slug: String!) {
    contentfulMokomojiMedziaga(slug: { eq: $slug }) {
      title
      author
      description {
        raw
      }
      shopifyProduct
      toc {
        type
        title
      }
    }
    allShopifyProduct {
      nodes {
        title
        description
        vendor
        productType
        featuredMedia {
          mediaContentType
          ... on ShopifyMediaImage {
            id
            image {
              gatsbyImageData(formats: [AUTO, WEBP], width: 843)
            }
          }
        }
        metafields {
          key
          value
        }
        variants {
          shopifyId
          storefrontId
          presentmentPrices {
            price {
              amount
              currencyCode
            }
          }
        }
      }
    }
    allContentfulPuslapis(
      filter: { layout: { eq: "Mokomosios medžiagos paieška" } }
    ) {
      nodes {
        title
        node_locale
        slug
        contentful_id
      }
    }
  }
`;

const InfoCardRow = ({ label, items }) => (
  <Box>
    <Box>
      <Label>{label}</Label>
    </Box>
    <Box>
      {items &&
        items.length &&
        items.map((item) => <Badge key={item}>{item}</Badge>)}
    </Box>
  </Box>
);

const SummaryItemTypes = {
  Video: { icon: <FileTypeVideoIcon />, color: 'primaryPaleBg' },
  Failas: { icon: <FileTypePdfIcon />, color: '#F5F5F5' },
  Default: { icon: <FileTypeDefaultIcon />, color: '#f5f5f5' },
};

const SummaryItem = ({ icon, text, bg }) => (
  <Flex
    sx={{
      backgroundColor: bg,
      borderRadius: '10px',
      padding: '20px',
      marginBottom: '8px',
      gap: '12px',
    }}
  >
    <Box sx={{ width: '40px', flexShrink: 0, textAlign: 'center' }}>{icon}</Box>
    <Box>{text}</Box>
  </Flex>
);

const MaterialContentLayout = ({ children, bg }) => (
  <Box bg={bg} sx={{ position: 'relative' }}>
    <Container px={4}>
      <Grid
        sx={{ gap: [0, null, null, '60px'] }}
        columns={[1, null, null, '3fr 2fr']}
      >
        {children}
      </Grid>
    </Container>
  </Box>
);

const formatGrades = (grades) => {
  try {
    const min = Math.min(...grades);
    const max = Math.max(...grades);

    if (min === max) {
      return [`${max}`];
    }

    return [`${min}-${max}`];
  } catch (error) {
    return [];
  }
};

const pageMaterial = ({ data, pageContext }) => {
  const { t, i18n } = useTranslation();
  const { locale } = pageContext;
  const location = useLocation();
  const [author, setAuthor] = useState({
    name: '',
    avatarUrl: '',
    link: '',
  });
  const [summaryItemsToShow, setSummaryItemsToShow] = useState(4);
  const checkoutModalPresenter = new CheckoutModalPresenter();
  const checkoutMaterialPresenter = new CheckoutMaterialPresenter();

  useEffect(() => {
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, []);

  const pageData = data.contentfulMokomojiMedziaga;

  const mainSearchPage = data.allContentfulPuslapis?.nodes.find(
    (node) => node.node_locale === (localeListInverted[locale] || locale)
  );

  const findProductVariant = (product) => {
    return (
      product.storefrontId === pageData.shopifyProduct ||
      convertBase64ToString(product.storefrontId) ===
        convertBase64ToString(pageData.shopifyProduct)
    );
  };

  const shopifyProduct = data.allShopifyProduct?.nodes?.find((product) => {
    return product.variants.find(findProductVariant);
  });

  const productVariant = shopifyProduct?.variants?.find(findProductVariant);

  useEffect(() => {
    if (!shopifyProduct || !productVariant) {
      navigate(`/${pageSlugs.notFound[locale]}`);
    }
  }, []);

  if (!shopifyProduct || !productVariant) {
    return <></>;
  }

  const { amount, currencyCode } = productVariant.presentmentPrices[0].price;

  const metaFields = {};

  shopifyProduct.metafields.forEach(({ key, value }) => {
    metaFields[key] = value;
  });

  const vm = {
    title: shopifyProduct?.title,
    image:
      shopifyProduct?.featuredMedia?.mediaContentType === 'IMAGE' &&
      getImage(shopifyProduct.featuredMedia.image),
    description: documentToReactComponents(
      JSON.parse(pageData.description.raw)
    ),
    subjects: (metaFields?.subject && JSON.parse(metaFields.subject)) || [],
    grades:
      (metaFields?.grade && formatGrades(JSON.parse(metaFields.grade))) || [],
    formats: (metaFields?.format && JSON.parse(metaFields.format)) || [],
    duration: [metaFields?.duration],
    hasSummary: pageData?.toc?.length > 0,
    summaryItems:
      (pageData?.toc &&
        pageData.toc.map(({ type, title }) => ({
          text: title,
          icon: SummaryItemTypes[type]?.icon || SummaryItemTypes.Default.icon,
          bg: SummaryItemTypes[type]?.color || SummaryItemTypes.Default.color,
        }))) ||
      [],
    buyButtonText: `${t('order')} (${amount} ${currencyCode})`,
    price: `${amount} ${currencyCode}`,
    shopifyId: productVariant.shopifyId,
  };

  useEffect(() => {
    // get author name and avatar
    const author = {};
    const loadTeacher = async (id) => {
      const res = await api.get(`teachers/${id}`);
      author.link = `${pageSlugs.teacher[pageContext.locale]}?id=${
        metaFields.teacher_id
      }`;
      author.name = res.displayName;
      author.avatarUrl = res.avatarUrl;
      setAuthor({
        name: author.name,
        link: author.link,
        avatarUrl: author.avatarUrl,
      });
    };

    if (Number(shopifyProduct.vendor) > 0) {
      loadTeacher(Number(shopifyProduct.vendor));
    } else {
      author.name = shopifyProduct.vendor || 'Mokosi LT';
      setAuthor({
        name: author.name,
        link: author.link,
        avatarUrl: author.avatarUrl,
      });
    }
  }, []);

  return (
    <Layout
      locale={pageContext.locale}
      otherSlugs={pageContext.otherSlugs}
      currentSlug={pageContext.slug}
      navTheme="dark"
    >
      <Box mt={4} py={6}>
        <MaterialContentLayout bg="white">
          <Box>
            <Link sx={{ color: 'primary' }} to={`/${mainSearchPage.slug}`}>
              {`<`} {t('searchResults')}
            </Link>

            <Themed.h2 sx={{ lineHeight: 1.2 }}>{vm.title}</Themed.h2>
          </Box>
        </MaterialContentLayout>

        <MaterialContentLayout>
          <Flex py={4} sx={{ alignItems: 'center', gap: '8px' }}>
            {t('share')}

            <Themed.a
              sx={{ display: 'flex' }}
              href={'https://www.facebook.com/share.php?u=' + location.href}
              target="_blank"
            >
              <FacebookIcon />
            </Themed.a>

            <Themed.a
              sx={{ display: 'flex' }}
              href={'https://twitter.com/intent/tweet?text=' + location.href}
              target="_blank"
            >
              <TwitterIcon />
            </Themed.a>
          </Flex>
          <Flex py={4} sx={{ alignItems: 'center', gap: '24px' }}>
            <Box sx={{ position: 'relative', size: '80px' }}>
              <DefaultProfile size="80px" />

              {author.avatarUrl && (
                <Avatar
                  size="80px"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                  }}
                  src={author.avatarUrl}
                />
              )}
            </Box>

            {author.link && (
              <Link
                sx={{ color: 'text', fontSize: '24px' }}
                to={`/${author.link}`}
              >
                {author.name}
              </Link>
            )}

            {!author.link && (
              <Themed.a sx={{ color: 'text', fontSize: '24px' }}>
                {author.name}
              </Themed.a>
            )}
          </Flex>
        </MaterialContentLayout>

        <MaterialContentLayout bg="primaryPaleBg">
          <Box pb={4}>
            {vm.image && (
              <Box sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    borderRadius: '10px',
                    overflow: 'hidden',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <GatsbyImage
                    sx={{ width: '100%', height: '100%' }}
                    image={vm.image}
                    alt=""
                  />
                </Box>

                <Box
                  bg="white"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: '-200%',
                    right: '-200%',
                    height: '50%',
                    zIndex: 0,
                    pointerEvents: 'none',
                  }}
                />
              </Box>
            )}
            <Box py={3}>{vm.description}</Box>
          </Box>
          <Box pb={4} sx={{ position: 'relative' }}>
            <Card
              sx={{
                padding: '20px 40px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                position: 'relative',
                zIndex: 1,
              }}
            >
              {vm.grades.length > 0 && (
                <InfoCardRow
                  label={t('materialSummaryClass')}
                  items={vm.grades}
                />
              )}
              {vm.subjects.length > 0 && (
                <InfoCardRow label={t('subject')} items={vm.subjects} />
              )}
              <InfoCardRow
                label={t('materialSummaryFormat')}
                items={vm.formats}
              />
              <InfoCardRow
                label={t('materialSummaryDuration')}
                items={vm.duration}
              />

              <Button
                my={3}
                className="w-100"
                onClick={() => {
                  checkoutMaterialPresenter.setCheckoutData({
                    variantId: vm.shopifyId,
                    format: vm.formats,
                    duration: vm.duration,
                    productType: t('teachingMaterial'),
                    subject: vm.subjects,
                    class: vm.grades,
                    price: vm.price,
                    title: vm.title,
                    image: vm.image,
                    author: vm.authorName,
                  });
                  checkoutModalPresenter.toggleModal({
                    type: 'material',
                  });
                }}
              >
                {vm.buyButtonText}
              </Button>
            </Card>

            <Box
              bg="white"
              sx={{
                position: 'absolute',
                top: 0,
                left: '-200%',
                right: '-200%',
                height: '25%',
                zIndex: 0,
                pointerEvents: 'none',
              }}
            />

            {vm.hasSummary && (
              <Card sx={{ padding: '20px 40px', marginTop: 4 }}>
                <Themed.h4
                  sx={{
                    marginTop: 0,
                    marginLeft: '72px',
                    marginBottom: 3,
                  }}
                >
                  {t('contents')}
                </Themed.h4>

                {vm.summaryItems
                  .slice(0, summaryItemsToShow)
                  .map(({ text, icon, bg }, index) => (
                    <SummaryItem key={index} text={text} icon={icon} bg={bg} />
                  ))}

                {vm.summaryItems.length > summaryItemsToShow && (
                  <Box mt={3} py={3} sx={{ textAlign: 'center' }}>
                    <Themed.a
                      onClick={(e) => {
                        e.preventDefault();
                        setSummaryItemsToShow(100);
                      }}
                    >
                      {t('expandAll')}
                    </Themed.a>
                  </Box>
                )}
              </Card>
            )}
          </Box>
        </MaterialContentLayout>
      </Box>
    </Layout>
  );
};

export default pageMaterial;
